import type { CSSProperties, FC } from "react";
import { useContext } from "react";
import { tv } from "tailwind-variants";

import { directusContext } from "~/shared/utils/directus/directus.provider";

import {
  buildSrcSet,
  DEFAULT_CMS_IMAGE_QUALITY,
  DEFAULT_CMS_IMAGE_SIZES,
} from "./buildSrcSet";

export type CmsImageProps = {
  /** Primary image id (mobile fallback / smallest variant) */
  id: string;
  /** Optional tablet-variant image id (defaults to id) */
  tabletId?: string;
  /** Optional desktop-variant image id (defaults to tabletId ?? id) */
  desktopId?: string;
  /** Intrinsic image dimensions used for layout reservation. */
  width: number;
  height: number;
  alt: string;
  /**
   * If true, emits loading="eager" + fetchPriority="high" + decoding="sync".
   * Use ONLY for the LCP candidate (first hero on page). Default false.
   */
  priority?: boolean;
  loading?: "eager" | "lazy";
  /** Custom sizes attr; defaults to mobile-first responsive sizes (see DEFAULT_SIZES) */
  sizes?: string;
  className?: string;
  style?: CSSProperties;
};

const cmsImageStyles = tv({
  base: "block",
  variants: {
    priority: {
      true: "",
      false: "",
    },
  },
  defaultVariants: {
    priority: false,
  },
});

export const CmsImage: FC<CmsImageProps> = ({
  id,
  tabletId,
  desktopId,
  width,
  height,
  alt,
  priority = false,
  loading = "lazy",
  sizes = DEFAULT_CMS_IMAGE_SIZES,
  className,
  style,
}) => {
  const ctx = useContext(directusContext);
  if (!ctx) {
    throw new Error("CmsImage must be used within DirectusProvider");
  }
  const cmsPath = ctx.url;

  const tabletImageId = tabletId ?? id;
  const desktopImageId = desktopId ?? tabletImageId;

  // Keep this fallback aligned with resolveLcpImage preload fallback params.
  const fallbackSrc = `${cmsPath}/cms-assets/${id}?format=webp&width=800&quality=${DEFAULT_CMS_IMAGE_QUALITY}&z=.img`;

  return (
    <picture className={cmsImageStyles({ priority, className })} style={style}>
      <source
        media="(min-width: 1024px)"
        type="image/avif"
        srcSet={buildSrcSet(cmsPath, desktopImageId, "avif")}
        sizes={sizes}
      />
      <source
        media="(min-width: 768px)"
        type="image/avif"
        srcSet={buildSrcSet(cmsPath, tabletImageId, "avif")}
        sizes={sizes}
      />
      <source
        type="image/avif"
        srcSet={buildSrcSet(cmsPath, id, "avif")}
        sizes={sizes}
      />
      <source
        media="(min-width: 1024px)"
        type="image/webp"
        srcSet={buildSrcSet(cmsPath, desktopImageId, "webp")}
        sizes={sizes}
      />
      <source
        media="(min-width: 768px)"
        type="image/webp"
        srcSet={buildSrcSet(cmsPath, tabletImageId, "webp")}
        sizes={sizes}
      />
      <source
        type="image/webp"
        srcSet={buildSrcSet(cmsPath, id, "webp")}
        sizes={sizes}
      />
      <img
        src={fallbackSrc}
        alt={alt}
        width={width}
        height={height}
        loading={priority ? "eager" : loading}
        fetchPriority={priority ? "high" : "auto"}
        decoding={priority ? "sync" : "async"}
      />
    </picture>
  );
};
